/**
 * SC-02 — Insert generated text into the current slide (AIVA-6753).
 *
 * Adds ONE new text box to the selected slide and never touches existing
 * shapes, so a single Ctrl+Z in PowerPoint removes it (spec AC-2).
 *
 * Deliberately separate from `slideOps.insertTextOnSlide`: that helper is also
 * called by Deckorate's Get Insights flow, which is out of scope for the general
 * add-in, and it swallows every failure into `false`. This one differs in two
 * ways the action bar needs:
 *
 *  - Geometry comes from the real slide size (gap G-4). The prototype's fixed
 *    `50/240, 860×200` box assumed a 16:9 stage, so 4:3 and custom decks placed
 *    it badly. The same proportions are now applied to whatever size the deck
 *    reports, and the text auto-fits the box so a long reply cannot spill past
 *    the slide edge.
 *  - The box goes BELOW the content already on the slide ({@link placeBelowContent}).
 *    The spec's fixed position (44% down) only clears a title, so on a slide
 *    with body text the new box landed on top of it. The spec position is kept
 *    as the fallback for a slide with no room left.
 *  - Failures throw a named error ({@link NO_SLIDE_SELECTED_ERROR},
 *    {@link POWERPOINT_UNAVAILABLE_ERROR}) so the pill dispatcher can toast a
 *    message that says what to do, rather than a generic "Couldn't do that".
 */

/** Thrown when no slide is selected (e.g. Slide Sorter view with nothing picked). */
export const NO_SLIDE_SELECTED_ERROR = 'No slide selected';
/** Thrown when the pane is not running inside PowerPoint. */
export const POWERPOINT_UNAVAILABLE_ERROR = 'PowerPoint host unavailable';
/** Thrown when the text has nothing left to place once cleaned. */
export const NOTHING_TO_INSERT_ERROR = 'Nothing to insert';

/**
 * The stage the prototype's fixed geometry was designed on: 13.333in × 7.5in
 * (16:9) in points. Used as the fallback when the host cannot report the deck's
 * own size, which keeps the old placement exactly on those hosts.
 */
export const DEFAULT_SLIDE_SIZE = { width: 960, height: 540 } as const;

/** Font size for the inserted body text (spec SC-02 behaviour). */
export const INSERTED_TEXT_FONT_SIZE = 18;

export interface SlideSize {
  readonly width: number;
  readonly height: number;
}

export interface TextBoxGeometry {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Where the new text box goes, as a share of the slide.
 *
 * These are the prototype's `50/240, 860×200` on a 960×540 stage, turned into
 * proportions: the box starts 44% down, which clears a standard title
 * placeholder (T-SC02-2), and ends at 81%, inside the slide.
 */
export function textBoxGeometry(size: SlideSize): TextBoxGeometry {
  return {
    left: size.width * (50 / 960),
    top: size.height * (240 / 540),
    width: size.width * (860 / 960),
    height: size.height * (200 / 540),
  };
}

/** The vertical extent of a shape already on the slide, in points. */
export interface OccupiedBand {
  readonly top: number;
  readonly height: number;
}

/** Gap left between the existing content and the new box. */
export const CONTENT_GAP_RATIO = 0.02;
/** Bottom margin kept clear, so the box never touches the slide edge. */
export const BOTTOM_MARGIN_RATIO = 0.05;
/**
 * Below this much free height the slide counts as full. 12% of a 16:9 slide is
 * about 65pt — three lines of 18pt text before auto-fit starts shrinking it.
 */
export const MIN_BOX_HEIGHT_RATIO = 0.12;
/**
 * A shape at least this tall is treated as background (a full-bleed picture or
 * a frame), not content — counting it would leave no room on any such slide.
 */
export const BACKGROUND_HEIGHT_RATIO = 0.9;

/**
 * Where the new text box goes: under the lowest content already on the slide.
 *
 * Left and width keep the {@link textBoxGeometry} proportions so every inserted
 * box lines up the same way. The top sits just below the lowest content, and
 * the box fills the space down to the bottom margin — auto-fit shrinks the
 * text to that space, so a long reply still cannot leave the slide.
 *
 * Uses the spec position ({@link textBoxGeometry}) in two cases:
 *  - an empty slide — the text reads as body text under where a title would
 *    go, not text jammed against the top edge;
 *  - content that ends above the spec position (typically just a title) — the
 *    spec position already clears it, exactly as T-SC02-2 asks.
 *
 * Only what shows ON the slide counts: a shape lying wholly below the bottom
 * edge is ignored, and one running past it counts only down to the edge.
 *
 * A full slide — less than {@link MIN_BOX_HEIGHT_RATIO} free below the content —
 * gets a strip of that height at the bottom. The user asked for the text, so it
 * is placed rather than refused, and auto-fit shrinks it into the strip. It used
 * to get the spec position instead, which is mid-slide: on a slide whose body
 * text already reached the bottom that put the reply squarely on top of it.
 */
export function placeBelowContent(size: SlideSize, occupied: readonly OccupiedBand[]): TextBoxGeometry {
  const spec = textBoxGeometry(size);
  const content = occupied.filter(
    (s) => s.height > 0 && s.height < size.height * BACKGROUND_HEIGHT_RATIO && s.top < size.height
  );
  if (content.length === 0) return spec;

  const lowest = Math.min(Math.max(...content.map((s) => s.top + s.height)), size.height);
  const top = lowest + size.height * CONTENT_GAP_RATIO;
  if (top <= spec.top) return spec;

  const bottom = size.height * (1 - BOTTOM_MARGIN_RATIO);
  const minHeight = size.height * MIN_BOX_HEIGHT_RATIO;
  if (bottom - top < minHeight)
    return { left: spec.left, top: bottom - minHeight, width: spec.width, height: minHeight };
  return { left: spec.left, top, width: spec.width, height: bottom - top };
}

/**
 * Clean an AiVA reply for a slide (spec §4.1 / SC-02 AC-4).
 *
 * PowerPoint shows Markdown markers literally, so `**Q3**` would land on the
 * slide with its asterisks. The response contract already asks the model for
 * plain text; this is the half that does not depend on the model obeying it.
 * Bullet markers are dropped too: each line is already its own paragraph, and
 * a leading "- " reads as a stray dash on a slide.
 */
export function toSlideText(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/\*\*(.+?)\*\*/gs, '$1')
    .replace(/__(.+?)__/gs, '$1')
    .replace(/(^|[^*\w])\*(?!\s)([^*\n]+?)\*(?!\w)/g, '$1$2')
    .replace(/`([^`\n]*)`/g, '$1')
    .split('\n')
    .map((line) =>
      line
        .replace(/^\s{0,3}#{1,6}\s+/, '')
        .replace(/^\s*[-*•]\s+/, '')
        .trimEnd()
    )
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

interface PageSetupLike {
  slideWidth?: number;
  slideHeight?: number;
  load: (props: string) => void;
}

/**
 * The deck's slide size in points, or {@link DEFAULT_SLIDE_SIZE} when the host
 * cannot say.
 *
 * `presentation.pageSetup` is newer than the typings this project builds
 * against, so it is read defensively: older hosts simply do not have it. Read in
 * its own sync so a host that rejects it cannot take the insert down with it.
 * Also sizes SC-05's new slide (addAsNewSlide.ts).
 */
export async function readSlideSize(context: PowerPoint.RequestContext): Promise<SlideSize> {
  const pageSetup = (context.presentation as unknown as { pageSetup?: PageSetupLike }).pageSetup;
  if (!pageSetup || typeof pageSetup.load !== 'function') return DEFAULT_SLIDE_SIZE;
  try {
    pageSetup.load('slideWidth,slideHeight');
    await context.sync();
    const { slideWidth, slideHeight } = pageSetup;
    if (
      typeof slideWidth === 'number' &&
      slideWidth > 0 &&
      typeof slideHeight === 'number' &&
      slideHeight > 0
    ) {
      return { width: slideWidth, height: slideHeight };
    }
  } catch {
    /* page setup not supported on this host — fall back below */
  }
  return DEFAULT_SLIDE_SIZE;
}

/**
 * Shape types that show nothing once their text is gone. A placeholder shows
 * only "Click to add text"; a text box shows nothing at all. Anything else — a
 * rectangle, a picture — is visible content with or without text.
 */
const TEXT_ONLY_SHAPE_TYPES: ReadonlySet<string> = new Set(['placeholder', 'textbox']);

/**
 * The vertical extent of every shape that shows content on the slide.
 *
 * Empty placeholders and empty text boxes are skipped. A Title + Content
 * layout's unused body box covers most of the slide but shows only "Click to
 * add text", and a text box whose text was deleted shows nothing — counting
 * either would make the slide look full. The reported case was the second: text
 * boxes left behind by earlier inserts, emptied but not deleted, reached past
 * the bottom edge, so every new insert fell back to mid-slide, over the body
 * text. Whether a shape has text is read in its own sync, because shapes without
 * a text frame (pictures, charts) cannot answer — if that read fails, the shapes
 * are kept, which only ever moves the box lower, never onto content.
 *
 * Returns [] when the shapes cannot be read at all; the caller then uses the
 * spec position, exactly as before this read existed.
 */
async function readOccupiedBands(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide
): Promise<OccupiedBand[]> {
  try {
    const shapes = slide.shapes;
    shapes.load('items/type,items/top,items/height');
    await context.sync();
    const textOnly = shapes.items.filter((s) => TEXT_ONLY_SHAPE_TYPES.has(String(s.type).toLowerCase()));
    const empty = new Set<PowerPoint.Shape>();
    if (textOnly.length > 0) {
      try {
        for (const s of textOnly) s.textFrame.load('hasText');
        await context.sync();
        for (const s of textOnly) if (!s.textFrame.hasText) empty.add(s);
      } catch {
        /* text frame unreadable — keep every such shape as content */
      }
    }
    return shapes.items.filter((s) => !empty.has(s)).map((s) => ({ top: s.top, height: s.height }));
  } catch {
    return [];
  }
}

/**
 * Text formatting is best-effort: a host that refuses autosize or font size
 * still gets the text, which is the part the user asked for.
 */
function formatInsertedBox(shape: PowerPoint.Shape, asBullets: boolean): void {
  try {
    shape.textFrame.wordWrap = true;
    shape.textFrame.autoSizeSetting = 'AutoSizeTextToFitShape';
  } catch {
    /* autosize unavailable on this host */
  }
  try {
    shape.textFrame.textRange.font.size = INSERTED_TEXT_FONT_SIZE;
  } catch {
    /* font API unavailable on this host */
  }
  if (asBullets) {
    try {
      shape.textFrame.textRange.paragraphFormat.bulletFormat.visible = true;
    } catch {
      /* bullet API unavailable on this host — the lines still go in, unbulleted */
    }
  }
}

/**
 * Whether the text should go in as a bulleted list (AIVA-6809).
 *
 * The response contract writes slide content one bullet per line with no
 * Markdown, and {@link toSlideText} strips any "- " markers that slip through —
 * so without real PowerPoint bullets a list lands as bare lines. Two or more
 * lines is a list; a single line (a title, a headline) stays plain.
 */
export function isBulletList(slideText: string): boolean {
  return slideText.split('\n').filter((line) => line.trim()).length >= 2;
}

export interface InsertIntoSlideResult {
  readonly inserted: true;
  /** Id of the new shape, when the host reports it. */
  readonly shapeId?: string;
}

/**
 * Add `text` to the selected slide as a new text box.
 *
 * Throws {@link NO_SLIDE_SELECTED_ERROR}, {@link POWERPOINT_UNAVAILABLE_ERROR}
 * or {@link NOTHING_TO_INSERT_ERROR}; any other error is a host failure.
 */
export async function insertTextIntoCurrentSlide(text: string): Promise<InsertIntoSlideResult> {
  if (!hasPpt()) throw new Error(POWERPOINT_UNAVAILABLE_ERROR);
  const slideText = toSlideText(text);
  if (!slideText) throw new Error(NOTHING_TO_INSERT_ERROR);

  return await ppt().run(async (context) => {
    const selected = context.presentation.getSelectedSlides();
    selected.load('items/id');
    await context.sync();
    const slide = selected.items[0];
    if (!slide) throw new Error(NO_SLIDE_SELECTED_ERROR);

    const size = await readSlideSize(context);
    const occupied = await readOccupiedBands(context, slide);
    const shape = slide.shapes.addTextBox(slideText, placeBelowContent(size, occupied));
    formatInsertedBox(shape, isBulletList(slideText));
    shape.load('id');
    await context.sync();
    return { inserted: true as const, ...(shape.id ? { shapeId: shape.id } : {}) };
  });
}
